<template>
  <div class="ve-player-box" :style="{ transform: `scale(${autoScale && !embedded ? screenScale : 1})` }">
    <!-- <div v-if="showDevTools" class="ve-dev-toolbar">
      <span v-if="vePlayer" class="ve-dev-info">{{ currentProtocol }}</span>
    </div> -->

    <!-- Player container -->
    <div v-if="streamUrl" class="video-wrapper" ref="videoPlayerContainer">
      <div :key="playerKey" class="video-player-key-wrapper">
        <div :id="containerId" class="ve-player-container" ref="vePlayerContainer"></div>
      </div>

      <!-- Text Watermark (Type 1) -->
      <div
        v-if="
          watermarkVisible &&
          streamWatermarkData &&
          streamWatermarkData.watermarkType === 1 &&
          streamWatermarkData.watermark
        "
        class="video-layers"
        :class="`movement-${watermarkMovementType}`"
        :style="watermarkStyle"
      >
        {{ streamWatermarkData.watermark }}
      </div>

      <!-- Image Watermark (Type 2) -->
      <img
        v-if="
          watermarkVisible &&
          streamWatermarkData &&
          streamWatermarkData.watermarkType === 2 &&
          streamWatermarkData.watermarkImageUrl
        "
        :src="getWatermarkImageUrl()"
        class="video-layers video-layers-image"
        :class="`movement-${watermarkMovementType}`"
        :style="watermarkStyle"
        alt="watermark"
      />

      <!-- Text Watermark with configurable color & opacity (Type 5) -->
      <div
        v-if="
          watermarkVisible &&
          streamWatermarkData &&
          streamWatermarkData.watermarkType === 5 &&
          streamWatermarkData.watermark
        "
        class="video-layers"
        :class="`movement-${watermarkMovementType}`"
        :style="watermarkType5Style"
      >
        {{ streamWatermarkData.watermark }}
      </div>

      <!-- Text Watermark (Type 4) - text AND robust watermark -->
      <div
        v-if="
          watermarkVisible &&
          streamWatermarkData &&
          streamWatermarkData.watermarkType === 4 &&
          streamWatermarkData.watermark
        "
        class="video-layers"
        :class="`movement-${watermarkMovementType}`"
        :style="watermarkStyle"
      >
        {{ streamWatermarkData.watermark }}
      </div>

      <!-- Robust watermark overlay (Type 3: only robust, Type 4: robust + text) -->
      <div
        v-if="
          streamWatermarkData &&
          (streamWatermarkData.watermarkType === 3 || streamWatermarkData.watermarkType === 4) &&
          streamWatermarkData.watermark
        "
        class="robust-watermark-overlay"
        :style="robustWatermarkOverlayStyle"
      >
        {{ streamWatermarkData.watermark }}
      </div>
      <!-- Over the video, e.g. the bet placed / cancelled pill (video-toast.vue). -->
      <slot name="overlay" />
    </div>
    <slot v-else name="no-stream">
      <div class="no-active-arena">
        <img class="logo" v-if="!streamError" :src="require('@/assets/images/alert.webp')" />
        <img class="logo-out-of-balance" v-else :src="require('@/assets/images/out-of-money.png')" />
        <template v-if="streamError">
          <div class="text my-3">
            {{ $t('Stream locked') }}
          </div>
          <div class="text">
            {{ convertStreamErrorMessage(streamError) }}
          </div>
        </template>
        <template v-else>
          <div class="text">
            {{ $t('No fight at the moment') }}
          </div>
          <div class="text">
            {{ $t('Please come back again') }}
          </div>
        </template>
      </div>
    </slot>

    <div v-if="!embedded" :class="['video-banner-wrapper', { 'is-mobile': isMobile, reverse: isReverse }]">
      <div class="odds-wrapper">
        <span>{{ $t('Banker') }}</span>
        <span v-if="autoScale && screenScale > 1" class="ml-16 odds">{{ bankerOdds }}</span>
      </div>
      <div class="odds-wrapper">
        <span v-if="autoScale && screenScale > 1" class="mr-16 odds">{{ playerOdds }}</span>
        <span>{{ $t('Player') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
const ROBUST_WATERMARK_MODERATE = {
  interpolationSpeed: 0.02,
  targetDistanceThreshold: 3,
};

import { mapGetters, mapActions } from 'vuex';
import { FightStatus } from '@/utils/index.model';
import { isMobile } from 'mobile-device-detect';
import { find, isEmpty } from 'lodash';
import { getImgUrl, getBaseUrl, isProduction, getArenaStreamDrmType } from '@/utils/helpers';
import { getVePlayerLicense } from '@/config/ve-player-license';
import { reconnectMainConnection } from '@/utils/websocket';
import { createAntiPipLayer } from '@/utils/anti-pip';

export default {
  props: {
    isAnyPopupOpen: {
      type: Boolean,
      default: false,
    },
    videoPlayerReady: {
      type: Boolean,
      default: false,
    },
    // Arena whose stream is refreshed; defaults to the selected cockfight arena.
    arenaId: {
      type: String,
      default: '',
    },
    // Embedded in another product's layout: no Banker/Player banner, no fight auto-scale.
    embedded: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      screenScale: 1,
      isMobile,
      vePlayer: null,
      licenseConfigured: false,
      playerReady: false,
      playerKey: 0,
      containerId: `ve-player-container-${this._uid}`,
      currentProtocol: '',

      // Volume state carried across arena switches (player is recreated per arena)
      savedVolume: 0.6,
      savedMuted: true,
      volumeApplied: false,
      volumeReady: false,
      // After first successful play in this page session, restore full audio on arena switch/reinit
      playbackSessionReady: false,

      // Refresh to the live edge immediately after returning to the app.
      pauseStartTime: null,
      refreshThreshold: 20000,
      isReinitializing: false,
      pageHiddenTime: null,
      isRefreshingStream: false,
      ensureAutoPlayRetryCount: 0,
      pendingUrlAfterReinit: null,
      postRecreatePlayPending: false,
      lastResumeRefreshTime: 0,
      resumePlayFallbackTimer: null,
      playbackFailedNotified: false,

      showDevTools:
        process.env.NODE_ENV === 'development' ||
        (typeof window !== 'undefined' && window.location.search.includes('debug=true')),

      // Watermark frequency control
      watermarkVisible: false,
      watermarkTimer: null,
      watermarkMovementTimer: null,
      watermarkPosition: { x: 0, y: 0, targetX: undefined, targetY: undefined },
      robustWatermarkPosition: { x: 0, y: 0, targetX: undefined, targetY: undefined },
      robustWatermarkMoveTimer: null,
      currentCorner: 'top-left',
      cornerAnimationPhase: 'start',
      frequencyConfig: {
        0: { mode: 'off' },
        1: { mean_on_s: 2.4, mean_off_s: 21.6 },
        2: { mean_on_s: 2.8, mean_off_s: 11.2 },
        3: { mean_on_s: 3.2, mean_off_s: 7.47 },
        4: { mean_on_s: 3.6, mean_off_s: 5.4 },
        5: { mean_on_s: 4.0, mean_off_s: 4.0 },
        6: { mean_on_s: 4.4, mean_off_s: 2.93 },
        7: { mean_on_s: 4.8, mean_off_s: 2.06 },
        8: { mean_on_s: 5.2, mean_off_s: 1.3 },
        9: { mean_on_s: 5.6, mean_off_s: 0.62 },
        10: { mode: 'always_on' },
      },
    };
  },

  mounted() {
    // `pip: false` below only hides VePlayer's own button; the browser's PiP
    // (Safari, Chrome's media controls) still worked on its <video>. The layer
    // marks every video it finds and exits any PiP that starts anyway.
    this.antiPipLayer = createAntiPipLayer(this.pipDisabled);
    this.configureLicense();
    this.loadSavedVolume();

    this.$nextTick(() => {
      this.initPlayer();
      this.startWatermarkFrequency();
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pageshow', this.onPageShow);
    window.addEventListener('pagehide', this.onPageHide);
  },

  beforeDestroy() {
    if (this.antiPipLayer) {
      this.antiPipLayer.destroy();
      this.antiPipLayer = null;
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('pagehide', this.onPageHide);
    this.clearResumePlayFallback();
    this.stopWatermarkFrequency();
    this.stopContinuousMovement();
    this.stopRobustWatermarkMovement();
    this.destroyPlayer();
  },

  computed: {
    ...mapGetters('fight', ['fight']),
    ...mapGetters({
      arena: 'general/selectedArena',
      autoScale: 'general/videoAutoScale',
      config: 'general/config',
      globalStreamUrl: 'general/streamUrl',
      globalStreamWatermarkData: 'general/streamWatermarkData',
      globalStreamDrmData: 'general/streamDrmData',
      arenaStream: 'general/arenaStream',
      streamError: 'general/streamError',
      pipDisabled: 'general/pipDisabled',
      member: 'general/member',
      token: 'general/token',
    }),
    streamArenaId() {
      return this.arenaId || (this.arena && this.arena.id) || '';
    },
    /*
     * Prefer this player's own arena stream when one has been recorded, so two
     * players on screen (the desktop split view) do not both follow the single
     * most-recent global. Falls back to the global for every existing caller,
     * which keeps mobile and the v1 desktop client on their current behaviour.
     */
    ownArenaStream() {
      return this.arenaStream(this.streamArenaId);
    },
    streamUrl() {
      /*
       * An explicit arenaId means this player owns one specific arena, so it
       * must never fall back to the shared "most recent response" global -- on
       * first load that is whichever arena replied last, which showed both
       * desktop panes the same feed. Empty until this arena's own reply lands.
       */
      if (this.arenaId) return (this.ownArenaStream && this.ownArenaStream.url) || '';
      // No arenaId (the /v1 page): prefer this arena's own reply, so a reply for
      // another arena landing in the shared global cannot switch the channel.
      // The global is only the fallback until this arena's reply arrives.
      return (this.ownArenaStream && this.ownArenaStream.url) || this.globalStreamUrl;
    },
    streamWatermarkData() {
      if (this.arenaId) return this.ownArenaStream || {};
      if (this.ownArenaStream && this.ownArenaStream.url) return this.ownArenaStream;
      return this.globalStreamWatermarkData;
    },
    streamDrmData() {
      if (this.arenaId) return this.ownArenaStream || {};
      if (this.ownArenaStream && this.ownArenaStream.url) return this.ownArenaStream;
      return this.globalStreamDrmData;
    },
    isReverse() {
      return this.arena && this.arena.isReverse;
    },
    bankerOdds() {
      const banker = find(this.fight.selections, (s) => s.name === 'banker');
      return banker && !isEmpty(banker) ? `${banker.odds}x` : '';
    },
    playerOdds() {
      const player = find(this.fight.selections, (s) => s.name === 'player');
      return player && !isEmpty(player) ? `${player.odds}x` : '';
    },
    isMobileLandscape() {
      return isMobile && this.$screen.width > this.$screen.height;
    },
    watermarkMovementType() {
      return this.streamWatermarkData.watermarkMovement || 0;
    },
    watermarkStyle() {
      const type = this.watermarkMovementType;
      const fontSizeFromBE =
        this.streamWatermarkData.watermarkFontSize != null &&
        !Number.isNaN(Number(this.streamWatermarkData.watermarkFontSize)) &&
        Number(this.streamWatermarkData.watermarkFontSize) > 0
          ? Math.round(Number(this.streamWatermarkData.watermarkFontSize))
          : null;

      if (type === 0 || type === 1) {
        const style = {
          top: `${this.watermarkPosition.y}%`,
          left: `${this.watermarkPosition.x}%`,
        };
        if (fontSizeFromBE !== null) style.fontSize = `${fontSizeFromBE}px`;
        if (type === 0) style.transition = 'top 0.3s ease, left 0.3s ease';
        return style;
      }

      if (type === 3) {
        const style = { transition: 'top 2s ease, left 2s ease, opacity 0.3s ease' };
        if (fontSizeFromBE !== null) style.fontSize = `${fontSizeFromBE}px`;
        if (this.currentCorner === 'top-left') {
          if (this.cornerAnimationPhase === 'start') {
            style.top = '25%';
            style.left = '0%';
            style.opacity = '0';
          } else {
            style.top = '0%';
            style.left = '15%';
            style.opacity = '1';
          }
        } else {
          if (this.cornerAnimationPhase === 'start') {
            style.top = '100%';
            style.left = '75%';
            style.opacity = '0';
          } else {
            style.top = '75%';
            style.left = '95%';
            style.opacity = '1';
          }
        }
        return style;
      }

      return fontSizeFromBE !== null ? { fontSize: `${fontSizeFromBE}px` } : {};
    },
    robustWatermarkOverlayStyle() {
      return {
        top: `${this.robustWatermarkPosition.y}%`,
        left: `${this.robustWatermarkPosition.x}%`,
      };
    },
    watermarkType5Style() {
      const baseStyle = this.watermarkStyle;
      const watermarkColor = this.streamWatermarkData.watermarkColor || '#FFFFFF';
      const watermarkOpacity = this.streamWatermarkData.watermarkOpacity || 0.4;
      return { ...baseStyle, color: watermarkColor, opacity: watermarkOpacity };
    },
  },

  methods: {
    ...mapActions('general', ['updateGeneral', 'getArenaStreamUrl']),
    getImgUrl,
    getBaseUrl,

    configureLicense() {
      if (this.licenseConfigured) return;
      if (typeof window.VePlayer === 'undefined') return;

      window.VePlayer.live.setLicenseConfig({
        license: getVePlayerLicense(),
      });

      this.licenseConfigured = true;
    },

    getWatermarkImageUrl() {
      if (!this.streamWatermarkData || !this.streamWatermarkData.watermarkImageUrl) return '';
      const imageUrl = this.streamWatermarkData.watermarkImageUrl;
      const baseUrl = getBaseUrl();
      const separator = imageUrl.startsWith('/') ? '' : '/';
      return `https://${isProduction() ? 'api' : 'gc-api-dev'}.${baseUrl}${separator}${imageUrl}`;
    },

    inferVeStreamProtocol(u) {
      if (!u) return null;
      const s = String(u).toLowerCase();
      if (/\.sdp(\?|$)/.test(s) || s.includes('webrtm')) return 'rtm';
      if (/\.flv(\?|$)/.test(s)) return 'flv';
      if (/\.m3u8(\?|$)/.test(s)) return 'hls';
       if (/\.mpd(\?|$)/.test(s)) return 'dash'; 
      return null;
    },

    buildPlaylistAndFallback(url) {
      const primaryProtocol = this.inferVeStreamProtocol(url);
      let flvUrl = url;
      let hlsUrl = url;

      if (primaryProtocol === 'hls') {
        flvUrl = url.replace(/\.m3u8(\?.*)?$/i, '.flv');
      } else if (primaryProtocol === 'flv') {
        hlsUrl = url.replace(/\.flv(\?.*)?$/i, '.m3u8');
      } else if (primaryProtocol === 'rtm') {
        if (/_webrtm\.sdp/i.test(url)) {
          flvUrl = url.replace(/_webrtm\.sdp(\?.*)?$/i, '.flv');
          hlsUrl = url.replace(/_webrtm\.sdp(\?.*)?$/i, '.m3u8');
        } else {
          flvUrl = url.replace(/\.sdp(\?.*)?$/i, '.flv');
          hlsUrl = url.replace(/\.sdp(\?.*)?$/i, '.m3u8');
        }
      }

      const definitions = [];
      const pushDef = (u) => {
        if (u && definitions.indexOf(u) === -1) definitions.push(u);
      };

      if (primaryProtocol === 'rtm') {
        pushDef(url);
        if (flvUrl !== url) pushDef(flvUrl);
        if (hlsUrl !== url && hlsUrl !== flvUrl) pushDef(hlsUrl);
      } else if (primaryProtocol === 'flv') {
        pushDef(url);
        if (hlsUrl !== url) pushDef(hlsUrl);
      } else if (primaryProtocol === 'hls') {
        pushDef(url);
        if (flvUrl !== url) pushDef(flvUrl);
      } else {
        pushDef(url);
      }

      const present = definitions.map((d) => this.inferVeStreamProtocol(d)).filter(Boolean);
      const unique = [...new Set(present)];
      const primary = primaryProtocol || unique[0] || 'hls';
      const fallbackOrder = [primary, ...unique.filter((p) => p !== primary)];

      return { definitions, fallback: { fallbackOrder } };
    },

    normalizeSignedDrmUrl(rawUrl) {
      if (!rawUrl) return '';
      const value = String(rawUrl).trim();
      if (!value) return '';
      if (/^https?:\/\//i.test(value)) return value;
      if (/^\/\//.test(value)) return `https:${value}`;

      const queryString = value.startsWith('?') ? value.slice(1) : value;
      const looksLikeSignedQuery =
        /(?:^|&)x-algorithm=/i.test(queryString) ||
        /(?:^|&)x-signature=/i.test(queryString) ||
        /(?:^|&)x-credential=/i.test(queryString);

      if (looksLikeSignedQuery) {
        return `https://open.byteplusapi.com/?${queryString}`;
      }

      return value;
    },

    extractSignedHeadersFromDrmUrl(drmUrl) {
      if (!drmUrl) return {};
      try {
        const parsed = new URL(this.normalizeSignedDrmUrl(drmUrl));
        const query = {};
        parsed.searchParams.forEach((value, key) => {
          query[key.toLowerCase()] = value;
        });

        const algorithm = query['x-algorithm'];
        const credential = query['x-credential'];
        const signedHeaders = query['x-signedheaders'];
        const signature = query['x-signature'];
        const xDate = query['x-date'];
        const xContentSha256 = query['x-content-sha256'];

        if (!algorithm || !credential || !signedHeaders || !signature) return {};

        const headers = {
          Authorization: `${algorithm} Credential=${credential}, SignedHeaders=${signedHeaders}, Signature=${signature}`,
          'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
        };

        if (xDate) {
          headers['X-Date'] = xDate;
        }
        if (xContentSha256) {
          headers['X-Content-Sha256'] = xContentSha256;
        }
        return headers;
      } catch {
        return {};
      }
    },

    buildDrmConfig() {
      const drmLicenseUrl = this.normalizeSignedDrmUrl(this.streamDrmData && this.streamDrmData.drmLicenseUrl);
      const drmCertUrl = this.normalizeSignedDrmUrl(this.streamDrmData && this.streamDrmData.drmCertUrl);
      if (!drmLicenseUrl) {
        console.warn('[DRM DEBUG] buildDrmConfig early return: drmLicenseUrl is empty');
        return {};
      }

      const drmType = getArenaStreamDrmType();

      if (drmType === 'fairplay') {
        if (!drmCertUrl) {
          console.warn('[DRM DEBUG] buildDrmConfig early return: fairplay requires drmCertUrl');
          return {};
        }
        return {
          fairplay: {
            getDrmConfig: async () => {
              const certUrl = this.normalizeSignedDrmUrl(this.streamDrmData && this.streamDrmData.drmCertUrl);
              const licenseUrl = this.normalizeSignedDrmUrl(this.streamDrmData && this.streamDrmData.drmLicenseUrl);
              const certHeaders = this.extractSignedHeadersFromDrmUrl(certUrl);
              const headers = this.extractSignedHeadersFromDrmUrl(licenseUrl);
              return {
                serverCertificatePath: certUrl,
                serverProcessSPCPath: licenseUrl,
                ...(Object.keys(certHeaders).length ? { serverCertificateHeaders: certHeaders } : {}),
                ...(Object.keys(headers).length ? { serverProcessSPCHeaders: headers, headers } : {}),
              };
            },
          },
        };
      }

      return {
        widevine: {
          getDrmConfig: async () => {
            const licenseUrl = this.normalizeSignedDrmUrl(this.streamDrmData && this.streamDrmData.drmLicenseUrl);
            const headers = this.extractSignedHeadersFromDrmUrl(licenseUrl);
            return {
              serverProcessSPCPath: licenseUrl,
              ...(Object.keys(headers).length ? { serverProcessSPCHeaders: headers, headers } : {}),
            };
          },
        },
      };
    },

    buildPlayerConfig() {
      const url = this.streamUrl;
      if (!url) return null;

      const { definitions, fallback } = this.buildPlaylistAndFallback(url);

      // The SDK finds its container with getElementById, and several players
      // share the page in the desktop split view -- so each needs its own id.
      if (!document.getElementById(this.containerId)) {
        console.warn('[VePlayer] container not in the DOM yet:', this.containerId);
        return null;
      }

      const config = {
        id: this.containerId,
        width: '100%',
        height: '100%',
        autoplay: {
          muted: true,
          // Fall back to muted autoplay when unmuted autoplay is blocked (e.g. reload with sound on)
          enableDegradeMuteAutoplay: true,
          enableUserActionAutoplay: true,
        },
        // Hide center loading spinner and big play overlay — blank frame then play (like tc-video)
        ignores: ['loading', 'start'],
        isLive: true,
        controls: true,
        playsinline: true,
        videoInit: true,
        closeResizeObserver: true,

        pip: !this.pipDisabled,
        fullscreen: false,
        cssFullscreen: false,
        screenShot: false,
        download: false,
        rotate: false,
        playbackRate: false,
        mini: false,
        miniprogress: false,
        keyShortcut: false,
        enableContextmenu: false,

        // Playback can only be changed from the controls. Repeatedly pausing a
        // live stream can leave playback behind the live edge.
        closeVideoClick: true,
        closeVideoTouch: true,
        closeVideoDblclick: true,
        inactive: 3000,

        videoAttributes: {
          playsinline: true,
          'webkit-playsinline': true,
          'x5-video-player-type': 'h5',
          'x5-video-player-fullscreen': true,
        },
        logger: {
          appId: '1017733',
        },

        flvLowLatency: {
          enable: true,
          speedUpThreshold: 1.5,
          speedUpRate: 1.1,
          slowDownThreshold: 0.5,
          slowDownRate: 0.95,
        },

        error: {
          retryCount: 5,
          retryInterval: 3000,
        },

        playlist: [{ definitions }],
        fallback,
      };

      const drm = this.buildDrmConfig();
      if (Object.keys(drm).length) {
        config.drm = drm;
      }

      return config;
    },

    getPrimaryStreamUrl(url = this.streamUrl) {
      if (!url) return '';
      const { definitions } = this.buildPlaylistAndFallback(url);
      return definitions[0] || url;
    },

    async switchStreamUrl(url) {
      if (!this.vePlayer || !url) return false;

      const targetUrl = this.getPrimaryStreamUrl(url);
      if (!targetUrl) return false;

      const restoreAudio = () => this.restoreVolumeState();

      try {
        const kernel = this.getPlayerKernel();
        if (kernel && typeof kernel.switchURL === 'function') {
          const result = kernel.switchURL(targetUrl, { seamless: true });
          if (result && typeof result.then === 'function') {
            await result;
          }
          this.currentProtocol = this.detectProtocol();
          restoreAudio();
          this.vePlayer.once('canplay', restoreAudio);
          this.vePlayer.once('playing', restoreAudio);
          return true;
        }
        if (typeof this.vePlayer.switchURL === 'function') {
          const result = this.vePlayer.switchURL(targetUrl, { seamless: true });
          if (result && typeof result.then === 'function') {
            await result;
          }
          this.currentProtocol = this.detectProtocol();
          restoreAudio();
          this.vePlayer.once('canplay', restoreAudio);
          this.vePlayer.once('playing', restoreAudio);
          return true;
        }
      } catch (err) {
        console.warn('[VePlayer] switchURL failed, reinitializing:', err);
      }
      return false;
    },

    notifyPlaybackFailed() {
      if (this.playbackFailedNotified) return;
      this.playbackFailedNotified = true;
      this.$emit('playback-failed');
    },

    async initPlayer() {
      if (!this.streamUrl) return;
      if (typeof window.VePlayer === 'undefined') {
        console.error('[VePlayer] SDK not loaded');
        this.notifyPlaybackFailed();
        return;
      }

      this.destroyPlayer();
      this.playbackFailedNotified = false;
      this.configureLicense();
      this.playerReady = false;
      this.volumeApplied = false;
      this.volumeReady = false;
      this.pauseStartTime = null;

      try {
        const config = this.buildPlayerConfig();
        if (!config) return;

        this.vePlayer = await window.VePlayer.createLivePlayer(config);

        this.currentProtocol = this.detectProtocol();

        this.bindPlayerEvents();
        this.bindRefreshButton();
      } catch (err) {
        console.error('[VePlayer] Failed to create player:', err);
        this.notifyPlaybackFailed();
      }
    },

    bindPlayerEvents() {
      if (!this.vePlayer) return;

      this.vePlayer.on('playing', () => {
        this.clearResumePlayFallback();
        this.playerReady = true;
        this.playbackSessionReady = true;
        this.$emit('update:videoPlayerReady', true);
        this.applyInitialVolume();
        this.bindRefreshButton();
        if (this.postRecreatePlayPending) {
          this.postRecreatePlayPending = false;
          this.ensureAutoPlay(true);
        }
        this.restorePendingResumeAudio();
      });

      this.vePlayer.on('canplay', () => {
        this.playerReady = true;
        this.applyInitialVolume();
      });

      this.vePlayer.on('autoplayFail', () => {
        if (this.playbackSessionReady) {
          this.ensureAutoPlay();
        } else {
          this.ensureMutedAutoplay();
        }
      });

      this.vePlayer.on('volumechange', () => {
        if (!this.volumeReady) return;
        this.syncVolumeFromKernel();
        this.playbackSessionReady = true;
        this.persistVolume();
      });

      this.vePlayer.on('pause', () => {
        if (this.isReinitializing) return;
        this.pauseStartTime = Date.now();
      });

      this.vePlayer.on('play', () => {
        this.handleResume();
      });

      this.vePlayer.on('error', (error) => {
        const code = error && error.errorCode;
        console.error('[DRM DEBUG] VePlayer error payload', error);
        if (code === 7100) {
          console.error('[VePlayer] DRM license validation failed');
        }
      });

      this.vePlayer.on('core_event', (event) => {
        if (event && event.type === 'switch') {
          this.currentProtocol = this.detectProtocol();
        }
      });

      const LiveEvents = window.VePlayer && window.VePlayer.live && window.VePlayer.live.Events;
      if (LiveEvents && LiveEvents.FALLBACK_ERROR) {
        this.vePlayer.on(LiveEvents.FALLBACK_ERROR, () => {
          console.error('[VePlayer] All fallback protocols failed');
          this.notifyPlaybackFailed();
        });
      }
    },

    detectProtocol() {
      if (!this.vePlayer) return '';
      try {
        const src = this.vePlayer.src || '';
        if (/\.sdp(\?|$)/i.test(src) || src.toLowerCase().includes('webrtm')) return 'RTM';
        if (src.includes('.flv')) return 'FLV';
        if (src.includes('.m3u8')) return 'HLS';
        return 'Unknown';
      } catch (e) {
        return '';
      }
    },

    convertStreamErrorMessage(errorMessage) {
      const match = errorMessage.match(/([A-Z]{3})\s*(\d+)/);
      if (match) {
        const currentCurrency = match[1];
        const currentBalance = match[2];
        return this.$t('You need at least {CURRENCY} {AMOUNT} in your wallet to watch this stream.', {
          CURRENCY: currentCurrency,
          AMOUNT: currentBalance,
        });
      }
      return errorMessage;
    },

    destroyPlayer() {
      if (this.vePlayer) {
        try {
          this.vePlayer.destroy();
        } catch (e) {
          // Ignore
        }
        this.vePlayer = null;
      }
      this.playerReady = false;
    },

    getPlayerKernel() {
      if (!this.vePlayer) return null;
      return this.vePlayer.player || this.vePlayer;
    },

    loadSavedVolume() {
      try {
        const v = window.localStorage.getItem('veplayerVolume');
        const m = window.localStorage.getItem('veplayerMuted');
        if (v !== null && !Number.isNaN(Number(v))) {
          this.savedVolume = Math.min(1, Math.max(0, Number(v)));
        }
        if (m !== null) {
          this.savedMuted = m === 'true';
        }
      } catch (e) {
        // Ignore storage access errors
      }
    },

    persistVolume() {
      try {
        window.localStorage.setItem('veplayerVolume', String(this.savedVolume));
        window.localStorage.setItem('veplayerMuted', String(this.savedMuted));
      } catch (e) {
        // Ignore storage access errors
      }
    },

    syncVolumeFromKernel() {
      const kernel = this.getPlayerKernel();
      if (!kernel) return;
      if (typeof kernel.volume === 'number') this.savedVolume = kernel.volume;
      this.savedMuted = !!kernel.muted;
    },

    restoreVolumeState() {
      const kernel = this.getPlayerKernel();
      if (!kernel) return;
      try {
        kernel.volume = this.savedVolume;
        kernel.muted = this.savedMuted;
      } catch (e) {
        // Ignore if kernel not ready
      }
    },

    applyInitialVolume() {
      if (this.volumeApplied) return;
      const kernel = this.getPlayerKernel();
      if (!kernel) return;
      this.volumeApplied = true;
      try {
        kernel.volume = this.savedVolume;
        // Cold start: stay muted so autoplay succeeds (browser policy).
        // In-session (arena switch / reinit): restore the user's actual mute state.
        kernel.muted = this.playbackSessionReady ? this.savedMuted : true;
      } catch (e) {
        // Ignore if kernel not ready
      }
      this.$nextTick(() => {
        this.volumeReady = true;
      });
    },

    ensureMutedAutoplay() {
      const kernel = this.getPlayerKernel();
      if (!kernel) return;
      try {
        kernel.muted = true;
        const playResult = kernel.play && kernel.play();
        if (playResult && typeof playResult.catch === 'function') {
          playResult.catch(() => {});
        }
      } catch (e) {
        // Ignore if kernel not ready
      }
    },

    refreshLatestStreamOnResume({ reconnectWs = false } = {}) {
      if (!this.streamUrl) return;

      const now = Date.now();
      if (now - this.lastResumeRefreshTime < 1000) {
        if (reconnectWs && this.token) {
          reconnectMainConnection(this.token);
        }
        return;
      }
      this.lastResumeRefreshTime = now;

      if (reconnectWs && this.token) {
        reconnectMainConnection(this.token);
      }
      if (this.streamArenaId) {
        this.getArenaStreamUrl(this.streamArenaId);
      }

      const kernel = this.getPlayerKernel();
      if (this.currentProtocol === 'FLV' || this.inferVeStreamProtocol(this.streamUrl) === 'flv') {
        this.fallbackToMutedResume();
        return;
      }
      if (!kernel || this.isReinitializing) {
        this.fallbackToMutedResume();
        return;
      }

      this.clearResumePlayFallback();
      try {
        // Reuse the user-authorized media element and its audible playback state.
        kernel.volume = this.savedVolume;
        kernel.muted = this.savedMuted;

        if (!kernel.paused) return;

        const playResult = kernel.play && kernel.play();
        if (playResult && typeof playResult.then === 'function') {
          playResult.then(this.clearResumePlayFallback).catch(this.fallbackToMutedResume);
        }
      } catch (e) {
        this.fallbackToMutedResume();
        return;
      }

      // A few WebViews leave play() pending instead of rejecting it.
      this.resumePlayFallbackTimer = setTimeout(() => {
        this.resumePlayFallbackTimer = null;
        const currentKernel = this.getPlayerKernel();
        if (currentKernel && currentKernel.paused) {
          this.fallbackToMutedResume();
        }
      }, 1500);
    },

    clearResumePlayFallback() {
      if (this.resumePlayFallbackTimer) {
        clearTimeout(this.resumePlayFallbackTimer);
        this.resumePlayFallbackTimer = null;
      }
    },

    fallbackToMutedResume() {
      this.clearResumePlayFallback();
      if (this.isReinitializing || !this.streamUrl) return;

      const kernel = this.getPlayerKernel();
      const isFlv = this.currentProtocol === 'FLV' || this.inferVeStreamProtocol(this.streamUrl) === 'flv';
      if (kernel && !kernel.paused && !isFlv) return;
      if (kernel) {
        try {
          kernel.muted = true;
        } catch (e) {
          // Ignore if the backgrounded kernel is no longer available
        }
      }

      this.savedMuted = true;
      this.persistVolume();
      this.fullRecreatePlayer();
    },

    restorePendingResumeAudio() {
      if (!this.pendingResumeAudioState) return;

      const audioState = this.pendingResumeAudioState;
      this.pendingResumeAudioState = null;
      this.savedVolume = audioState.volume;
      this.savedMuted = audioState.muted;
      this.restoreVolumeState();
      this.persistVolume();
    },

  // Mute-first play on mobile when sound is on, then restore volume (browser autoplay policy).
    ensureAutoPlay(retryFromRecreate = false) {
      const kernel = this.getPlayerKernel();
      if (!kernel || !this.streamUrl) return;

      // Capture the user's preference before applying any temporary autoplay mute.
      // A volumechange event may otherwise change savedMuted before play() resolves.
      const desiredVolume = this.savedVolume;
      const desiredMuted = this.savedMuted;
      const restoreAudio = () => {
        try {
          kernel.volume = desiredVolume;
          kernel.muted = desiredMuted;
          this.savedVolume = desiredVolume;
          this.savedMuted = desiredMuted;
        } catch (e) {
          // Ignore if kernel not ready
        }
      };

      if (!kernel.paused) {
        if (this.playbackSessionReady && !desiredMuted && kernel.muted) {
          restoreAudio();
        }
        return;
      }

      try {
        // Resume the existing element with its current sound state. Only a newly
        // recreated player needs the muted-autoplay fallback.
        if (retryFromRecreate && isMobile && this.playbackSessionReady && !desiredMuted) {
          kernel.muted = true;
        }
        const playResult = kernel.play && kernel.play();
        if (playResult && typeof playResult.then === 'function') {
          playResult
            .then(() => {
              this.ensureAutoPlayRetryCount = 0;
              restoreAudio();
            })
            .catch(() => {
              if (retryFromRecreate && isMobile && this.ensureAutoPlayRetryCount < 3) {
                this.ensureAutoPlayRetryCount += 1;
                setTimeout(() => this.ensureAutoPlay(true), 1000);
              }
            });
        } else if (!kernel.paused) {
          restoreAudio();
        }
      } catch (e) {
        if (retryFromRecreate && isMobile && this.ensureAutoPlayRetryCount < 3) {
          this.ensureAutoPlayRetryCount += 1;
          setTimeout(() => this.ensureAutoPlay(true), 1000);
        }
      }
    },

    ensurePlayback() {
      this.ensureAutoPlay();
    },

    handleResume() {
      if (this.isReinitializing || !this.pauseStartTime) return;
      const pausedFor = Date.now() - this.pauseStartTime;
      this.pauseStartTime = null;
      if (pausedFor > this.refreshThreshold) {
        this.refreshStreamUrl();
      }
    },

    scheduleEnsureAutoPlay() {
      this.$nextTick(() => this.ensureAutoPlay(true));
      if (isMobile) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setTimeout(() => this.ensureAutoPlay(true), isIOS ? 300 : 100);
      }
    },

    onVisibilityChange() {
      if (document.hidden) {
        this.pageHiddenTime = Date.now();
        return;
      }

      if (!this.pageHiddenTime) {
        this.refreshLatestStreamOnResume();
        return;
      }

      const hiddenDuration = Date.now() - this.pageHiddenTime;
      this.pageHiddenTime = null;

      this.refreshLatestStreamOnResume({
        reconnectWs: hiddenDuration > 20000 && !!this.streamArenaId,
      });
    },

    onPageShow() {
      this.refreshLatestStreamOnResume();
    },

    onPageHide() {
      if (!this.pageHiddenTime) {
        this.pageHiddenTime = Date.now();
      }
    },

    refreshStreamUrl({ reconnectWs = false } = {}) {
      if (this.isRefreshingStream || !this.streamUrl) return;

      this.isRefreshingStream = true;
      setTimeout(() => {
        this.isRefreshingStream = false;
      }, 500);

      if (reconnectWs && this.token) {
        reconnectMainConnection(this.token);
      }

      this.fullRecreatePlayer();

      if (this.streamArenaId) {
        this.getArenaStreamUrl(this.streamArenaId);
      }
    },

    async fullRecreatePlayer() {
      if (this.isReinitializing || !this.streamUrl) return;
      this.isReinitializing = true;
      this.postRecreatePlayPending = true;
      try {
        this.syncVolumeFromKernel();
        this.playbackSessionReady = true;
        this.pauseStartTime = null;
        this.ensureAutoPlayRetryCount = 0;
        this.playerKey += 1;
        await this.$nextTick();
        await this.initPlayer();
        this.scheduleEnsureAutoPlay();

        if (this.pendingUrlAfterReinit) {
          const url = this.pendingUrlAfterReinit;
          this.pendingUrlAfterReinit = null;
          if (url !== this.getPrimaryStreamUrl()) {
            await this.switchStreamUrl(url);
          }
          this.scheduleEnsureAutoPlay();
        }
      } finally {
        this.isReinitializing = false;
      }
    },

    bindRefreshButton() {
      this.$nextTick(() => {
        const container = this.$refs.vePlayerContainer;
        if (!container) return;
        const btn = container.querySelector('.veplayer-refresh');
        if (!btn || btn._veRefreshBound) return;
        btn._veRefreshBound = true;
        const handler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.refreshStreamUrl();
        };
        btn.addEventListener('click', handler, true);
        btn.addEventListener('touchstart', handler, { capture: true, passive: false });
      });
    },

    getExponentialJitter(meanSeconds) {
      return -meanSeconds * Math.log(1 - Math.random());
    },

    startWatermarkFrequency() {
      if (!this.streamWatermarkData || typeof this.streamWatermarkData.watermarkFrequency === 'undefined') {
        this.watermarkVisible = false;
        this.stopContinuousMovement();
        return;
      }

      const frequency = this.streamWatermarkData.watermarkFrequency || 0;
      const config = this.frequencyConfig[frequency];
      if (!config) {
        this.watermarkVisible = false;
        this.stopContinuousMovement();
        return;
      }

      if (this.watermarkTimer) {
        clearTimeout(this.watermarkTimer);
        this.watermarkTimer = null;
      }

      if (config.mode === 'off') {
        this.watermarkVisible = false;
        this.stopContinuousMovement();
        return;
      }

      if (config.mode === 'always_on') {
        this.watermarkVisible = true;
        this.randomizeWatermarkPosition();
        this.startContinuousMovement();
        return;
      }

      this.watermarkVisible = true;
      this.randomizeWatermarkPosition();
      this.startContinuousMovement();
      this.scheduleWatermarkToggle();
    },

    scheduleWatermarkToggle() {
      const frequency = this.streamWatermarkData.watermarkFrequency || 0;
      const config = this.frequencyConfig[frequency];
      if (!config || config.mode === 'off' || config.mode === 'always_on') return;

      const delay = this.watermarkVisible
        ? this.getExponentialJitter(config.mean_on_s) * 1000
        : this.getExponentialJitter(config.mean_off_s) * 1000;

      this.watermarkTimer = setTimeout(() => {
        const wasVisible = this.watermarkVisible;
        this.watermarkVisible = !this.watermarkVisible;
        if (this.watermarkVisible && !wasVisible) this.randomizeWatermarkPosition();
        this.scheduleWatermarkToggle();
      }, delay);
    },

    stopWatermarkFrequency() {
      if (this.watermarkTimer) {
        clearTimeout(this.watermarkTimer);
        this.watermarkTimer = null;
      }
    },

    randomizeWatermarkPosition() {
      const type = this.watermarkMovementType;
      if (type === 2 || type === 3) return;
      this.watermarkPosition.x = Math.random() * 70;
      this.watermarkPosition.y = Math.random() * 70;
      if (type === 1) {
        this.watermarkPosition.targetX = Math.random() * 70;
        this.watermarkPosition.targetY = Math.random() * 70;
      }
    },

    randomizeRobustWatermarkPosition() {
      this.robustWatermarkPosition.x = 5 + Math.random() * 70;
      this.robustWatermarkPosition.y = 5 + Math.random() * 70;
      this.robustWatermarkPosition.targetX = 5 + Math.random() * 70;
      this.robustWatermarkPosition.targetY = 5 + Math.random() * 70;
    },

    startRobustWatermarkMovement() {
      this.stopRobustWatermarkMovement();
      this.randomizeRobustWatermarkPosition();

      const speed = ROBUST_WATERMARK_MODERATE.interpolationSpeed;
      const threshold = ROBUST_WATERMARK_MODERATE.targetDistanceThreshold;

      this.robustWatermarkMoveTimer = setInterval(() => {
        const pos = this.robustWatermarkPosition;
        if (pos.targetX === undefined || pos.targetY === undefined) {
          pos.targetX = 5 + Math.random() * 70;
          pos.targetY = 5 + Math.random() * 70;
        }
        const dx = pos.targetX - pos.x;
        const dy = pos.targetY - pos.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < threshold) {
          pos.targetX = 5 + Math.random() * 70;
          pos.targetY = 5 + Math.random() * 70;
        } else {
          pos.x += dx * speed;
          pos.y += dy * speed;
        }
      }, 50);
    },

    stopRobustWatermarkMovement() {
      if (this.robustWatermarkMoveTimer) {
        clearInterval(this.robustWatermarkMoveTimer);
        this.robustWatermarkMoveTimer = null;
      }
    },

    startContinuousMovement() {
      this.stopContinuousMovement();

      if (this.watermarkMovementType === 1) {
        this.watermarkMovementTimer = setInterval(() => {
          if (!this.watermarkVisible) return;
          if (this.watermarkPosition.targetX === undefined) {
            this.watermarkPosition.targetX = Math.random() * 70;
            this.watermarkPosition.targetY = Math.random() * 70;
          }
          const dx = this.watermarkPosition.targetX - this.watermarkPosition.x;
          const dy = this.watermarkPosition.targetY - this.watermarkPosition.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 1) {
            this.watermarkPosition.targetX = Math.random() * 70;
            this.watermarkPosition.targetY = Math.random() * 70;
          } else {
            this.watermarkPosition.x += dx * 0.05;
            this.watermarkPosition.y += dy * 0.05;
          }
        }, 50);
      }

      if (this.watermarkMovementType === 3) {
        this.currentCorner = 'top-left';
        this.cornerAnimationPhase = 'start';
        this.watermarkMovementTimer = setInterval(() => {
          if (!this.watermarkVisible) return;
          if (this.cornerAnimationPhase === 'start') {
            this.cornerAnimationPhase = 'end';
          } else {
            this.currentCorner = this.currentCorner === 'top-left' ? 'bottom-right' : 'top-left';
            this.cornerAnimationPhase = 'start';
          }
        }, 2000);
      }
    },

    stopContinuousMovement() {
      if (this.watermarkMovementTimer) {
        clearInterval(this.watermarkMovementTimer);
        this.watermarkMovementTimer = null;
      }
    },
  },

  watch: {
    'config.language': {
      handler(newVal, oldVal) {
        if (!newVal || !oldVal || !this.streamArenaId) return;
        this.getArenaStreamUrl(this.streamArenaId);
      },
    },
    streamUrl(newVal, oldVal) {
      if (!newVal) {
        this.destroyPlayer();
        return;
      }
      if (newVal !== oldVal) {
        this.$nextTick(async () => {
          if (this.isReinitializing) {
            this.pendingUrlAfterReinit = newVal;
            return;
          }
          if (oldVal && this.playerReady) {
            this.syncVolumeFromKernel();
            this.playbackSessionReady = true;
          }
          if (oldVal && (await this.switchStreamUrl(newVal))) {
            return;
          }
          this.initPlayer();
        });
      }
    },
    streamDrmData: {
      handler(newVal, oldVal) {
        if (!newVal || !this.streamUrl || !oldVal) return;
        const newLicense = (newVal && newVal.drmLicenseUrl) || '';
        const oldLicense = (oldVal && oldVal.drmLicenseUrl) || '';
        if (newLicense && oldLicense && newLicense !== oldLicense && !this.isReinitializing) {
          this.$nextTick(() => this.fullRecreatePlayer());
        }
      },
      deep: true,
    },
    streamWatermarkData: {
      handler(newVal) {
        if (!newVal) return;
        this.stopWatermarkFrequency();
        this.startWatermarkFrequency();
        const watermarkType = Number((newVal && newVal.watermarkType) || 1);
        if (watermarkType === 3 || watermarkType === 4) {
          this.startRobustWatermarkMovement();
        } else {
          this.stopRobustWatermarkMovement();
        }
      },
      deep: true,
      immediate: true,
    },
    'fight.status': {
      handler(newVal) {
        if (this.isAnyPopupOpen || this.embedded) return;
        if (FightStatus[newVal] === FightStatus.started) {
          if (!isMobile && this.$refs.videoPlayerContainer) {
            this.screenScale = 1 + 285 / (this.$refs.videoPlayerContainer.clientHeight + 50);
          } else if (this.isMobileLandscape && this.$refs.videoPlayerContainer) {
            this.screenScale = this.$screen.height / this.$refs.videoPlayerContainer.clientHeight;
          } else {
            this.screenScale = 1;
          }
        } else {
          this.screenScale = 1;
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss">
.ve-player-box {
  min-height: 200px;
  position: relative;
  z-index: 1002;
  transform-origin: left top;
  transition: all 1s ease;

  .ve-dev-toolbar {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 8px;

    .ve-dev-info {
      background: rgba(0, 0, 0, 0.7);
      color: #0f0;
      padding: 2px 6px;
      font-size: 11px;
      border-radius: 3px;
      font-family: monospace;
    }
  }

  .video-player-key-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .no-active-arena {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: #0d1a24;
    aspect-ratio: 16/9;

    @supports not (aspect-ratio: 16/9) {
      &::before {
        float: left;
        padding-top: 56.25%;
        content: '';
      }

      &::after {
        display: block;
        content: '';
        clear: both;
      }
    }

    img {
      height: 6.4rem;
      width: 6.4rem;
    }

    .text {
      text-align: center;
      font-size: 16px;
      color: #b2d6ea;

      &.my-3 {
        margin-top: 1rem;
        margin-bottom: 1rem;
      }
    }
  }

  .video-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 16/9;
    background-color: #0d1a24;
    overflow: hidden;
    overscroll-behavior: none;
    touch-action: manipulation;

    @supports not (aspect-ratio: 16/9) {
      &::before {
        float: left;
        padding-top: 56.25%;
        content: '';
      }

      &::after {
        display: block;
        content: '';
        clear: both;
      }
    }

    .ve-player-container {
      width: 100%;
      height: 100%;
      overflow: hidden;

      video {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      /* VePlayer (BytePlus) — hide autoplay-mute overlay; xgplayer class kept for compatibility */
      .veplayer-unmute,
      .veplayer-unmute-bt,
      .xgplayer-unmute {
        display: none !important;
        pointer-events: none !important;
      }

      /* Arena switch / buffer: keep frame blank (no spinner or center play button) */
      .xgplayer-loading,
      .veplayer-loading,
      xg-loading,
      .xgplayer-start,
      .xgplayer-enter,
      .xgplayer-replay {
        display: none !important;
        pointer-events: none !important;
      }

      /* Match tc-video control placement: refresh on left, volume + play on right */
      .vjs-control-bar2 {
        display: flex !important;
        flex-wrap: wrap;
        align-items: center;

        .vjs-volume-panel,
        .vjs-volume-menu-button {
          order: 1 !important;
          margin-left: auto !important;
        }

        .vjs-play-control {
          order: 2 !important;
        }
      }

      .vjs-control-bar {
        display: flex !important;
        flex-wrap: wrap;
        align-items: center;

        .vjs-progress-control.vjs-control {
          order: 0 !important;
          flex: 1 1 auto !important;
          min-width: 4em;
        }

        .vjs-volume-panel,
        .vjs-volume-menu-button {
          order: 1 !important;
          margin-left: auto !important;
        }

        .vjs-play-control {
          order: 2 !important;
        }
      }

      /* VePlayer (XG) layout: refresh in the left corner, and volume | play
         in the right corner. The container div itself is the .xgplayer root,
         so controls are direct descendants. Each control is pinned explicitly
         to the control bar (.xg-inner-controls is position:absolute). */
      .xg-inner-controls {
        left: 0 !important;
        right: 0 !important;
      }

      /* The grids are position:relative by default, which would make the
         absolutely-positioned controls below anchor to a grid instead of the
         full control bar. Make them static so offsets are bar-relative. */
      .xg-left-grid,
      .xg-right-grid {
        position: static !important;
      }

      /* Refresh: pin to the far left corner */
      .veplayer-refresh {
        position: absolute !important;
        left: 15px;
        top: 50%;
        transform: translateY(-50%);
        margin: 0 !important;
        z-index: 2;
      }

      /* Time: just to the right of the refresh button */
      .xgplayer-time {
        position: absolute !important;
        left: 48px;
        top: 50%;
        transform: translateY(-50%);
        margin: 0 !important;
      }

      /* Volume: right corner, sits to the left of the play button */
      .xgplayer-volume {
        position: absolute !important;
        right: 50px;
        top: 50%;
        transform: translateY(-50%);
        margin: 0 !important;
        z-index: 2;
      }

      /* Play/pause: the far right corner */
      .xgplayer-play {
        position: absolute !important;
        right: 15px;
        top: 50%;
        transform: translateY(-50%);
        margin: 0 !important;
        z-index: 2;
      }

      /* Progress bar hit-area spans the full control bar and can paint over
         corner buttons when the bar is active/hovered. Keep buttons on top. */
      .xg-center-grid {
        pointer-events: none !important;

        .xgplayer-progress {
          pointer-events: auto !important;
        }
      }

      .veplayer-refresh,
      .xgplayer-volume,
      .xgplayer-play {
        z-index: 6 !important;
        pointer-events: auto !important;
      }

      /* Mobile only — the container gets .xgplayer-mobile on itself */
      &.xgplayer-mobile {
        xg-trigger .gradient,
        xg-trigger .time-preview {
          display: none !important;
        }

        .xgplayer-time {
          display: none !important;
        }
      }
    }
  }

  /* Same robust/text watermark look as tc-video-v1; .video-wrapper overflow clips to frame */
  .robust-watermark-overlay {
    position: absolute;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 999;
    color: #ffffff;
    font-size: clamp(14vw, 18vh, 100px);
    /* moderate preset: opacity 0.15, blurAmount 6 */
    opacity: 0.25;
    filter: blur(6px);
    font-family: Arial, sans-serif;
    font-weight: 600;
    user-select: none;
    white-space: nowrap;
    text-shadow: 0 0 8px rgba(0, 0, 0, 0.5);
    will-change: top, left;
  }

  .video-layers {
    position: absolute;
    color: #ff0000;
    font-size: 30px;
    font-family: Arial, sans-serif;
    font-weight: 500;
    pointer-events: none;
    z-index: 1000;
    white-space: nowrap;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
    user-select: none;

    &.movement-0 {
      transition: top 0.3s ease, left 0.3s ease;
    }

    &.movement-1 {
      will-change: top, left;
    }

    &.movement-2 {
      animation: marqueeScroll 20s linear infinite;
      left: 100%;
      top: 50%;
      transform: translateY(-50%);
    }

    &.movement-3 {
      transition: top 1.5s ease, left 1.5s ease;
      will-change: top, left;
    }
  }

  .video-layers-image {
    max-width: 150px;
    max-height: 150px;
    width: auto;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.7));

    &.movement-2 {
      max-width: 200px;
      max-height: 100px;
    }
  }

  @keyframes marqueeScroll {
    0% {
      transform: translateX(0) translateY(-50%);
    }
    100% {
      transform: translateX(-100vw) translateY(-50%);
    }
  }

  .video-banner-wrapper {
    display: flex;
    justify-content: space-between;
    padding: 0 24px;
    height: 48px;
    box-sizing: border-box;
    align-items: center;
    background-image: url('~@/assets/svg/video-banner.svg');
    background-position: center;
    background-size: cover;
    font-size: 12px;
    font-weight: 600;
    line-height: 40px;
    color: #fafafa;
    text-transform: uppercase;

    &.is-mobile {
      display: none;
    }

    .odds-wrapper {
      display: flex;
      align-items: center;

      .odds {
        font-size: 22px;
        color: #fff;
        text-transform: none;
        user-select: none;
      }
    }

    &.reverse {
      flex-direction: row-reverse;
      background-image: url('~@/assets/svg/video-banner-reverse.svg');
    }
  }
}
</style>
